import React from 'react';
import { Button, Text, Title } from '@mantine/core';
import { IconDots } from '@tabler/icons-react';

import TableActions from './TableActions';
import Loader from './Loader';
import useWindowDimensions from '../../hooks/useWindowDimensions';

type Props = {
    title: string;
    labels?: {
        color: string;
        label: string;
        value: string | number;
    }[];
    actions?: Array<{
        label: string;
        icon: React.ReactNode;
        onClick: () => void;
    }>;
    timeFrame: string;
    children: React.ReactNode;
    isLoading?: boolean;
};

function GraphCard({
    title,
    labels,
    actions,
    timeFrame,
    children,
    isLoading = false,
}: Props) {
    const { width } = useWindowDimensions();
    return (
        <div className="lg:h-[430px] h-[500px] flex flex-col flex-1 bg-white gap-5 shadow-md rounded-xl">
            <div className="flex justify-between items-center p-5 pb-0">
                <div className="overflow-hidden flex flex-col flex-1 gap-1">
                    <Title
                        order={3}
                        className="whitespace-nowrap text-ellipsis overflow-hidden"
                    >
                        {title}
                    </Title>
                    <Text className="text-xs text-gray-400">{timeFrame}</Text>
                </div>
                {actions && (
                    <TableActions row={{}} options={actions}>
                        <Button
                            variant="subtle"
                            className="text-primary rounded-md p-1 w-min h-min"
                        >
                            <IconDots />
                        </Button>
                    </TableActions>
                )}
            </div>
            {isLoading ? (
                <Loader />
            ) : (
                <>
                    <div
                        className={`flex flex-1 items-center justify-center w-full ${
                            labels ? 'max-h-[250px]' : ''
                        }`}
                    >
                        {children}
                    </div>
                    {labels?.length && (
                        <div
                            style={{
                                borderTop: '1px solid #BDC9D3',
                            }}
                            className="flex flex-col lg:flex-row items-start justify-evenly lg:items-center mb-3 pt-3 mx-5 gap-5"
                        >
                            {labels.map((label, index) => (
                                <div
                                    key={label.label}
                                    style={{
                                        borderLeft: `${
                                            index > 0 && width >= 1024 ? 2 : 0
                                        }px solid #BDC9D3`,
                                        paddingLeft: width < 1024 ? 10 : 24,
                                        gap: 8,
                                    }}
                                    className="flex flex-1 lg:block overflow-hidden"
                                >
                                    <div className="flex flex-1 items-center gap-2 h-full max-w-full">
                                        <div
                                            className="w-3 h-3 rounded-full"
                                            style={{
                                                backgroundColor: label.color,
                                            }}
                                        />
                                        <Text className="text-sm whitespace-nowrap overflow-hidden text-ellipsis">
                                            {label.label}
                                        </Text>
                                    </div>
                                    <Text className="text-sm font-bold">
                                        {label.value}
                                    </Text>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default GraphCard;
